import React from 'react';
import { CheckIcon, DownloadIcon, ShieldCheckIcon, StarIcon } from 'lucide-react';
import { CtaButton } from './CtaButton';
import { Reveal } from './Reveal';
import { CORE_PRICE, HERO_IMAGE, SALE_PRICE, standards } from '../data/content';
import { HERO_SIZES, HERO_WIDTHS, optimizedImage, optimizedSrcSet } from '../utils/images';
const proofPoints = ['Instant access', 'Lifetime updates', 'Secure checkout'];
export function Hero() {
  return <header className="relative w-full overflow-hidden border-b border-white/5 bg-steel-950" data-id="element-88" data-mp="128pi5dka8x4k">
      <div className="blueprint-grid absolute inset-0" aria-hidden="true" data-id="element-89" data-mp="xaxityhb68qc" />
      <div className="absolute inset-x-0 top-0 h-px bg-ember-500" aria-hidden="true" data-id="element-90" data-mp="sa9rtct2xrw4" />

      <div className="relative mx-auto flex max-w-4xl flex-col items-center px-6 pb-8 pt-5 text-center sm:pt-7" data-id="element-91" data-mp="nczr3108ptsz">
        <Reveal className="flex w-full flex-col items-center" data-id="element-92" data-mp="3d2ldtocrhr6i">
          <span className="inline-flex items-center border border-ember-500/40 bg-ember-500/10 px-3 py-1.5 text-[0.62rem] font-semibold uppercase tracking-[0.22em] text-ember-300 sm:text-[0.68rem]" data-id="element-93" data-mp="382p4rap7j9k3">
            Industrial Instrumentation Instructor System 2026
          </span>

          <h1 className="mt-3 font-display text-[2.1rem] font-bold uppercase leading-[0.94] tracking-tight text-ink-50 sm:text-5xl lg:text-6xl" data-id="element-94" data-mp="3o59bpicfn4e0">
            Teach The Plant Floor.
            <span className="block text-ember-400" data-id="element-95" data-mp="3e7l7m1sxskj6">Everything Ready.</span>
          </h1>
        </Reveal>

        <Reveal index={1} className="w-full" data-id="element-96" data-mp="30fy7trzsctvn">
          <img src={optimizedImage(HERO_IMAGE, 720)} srcSet={optimizedSrcSet(HERO_IMAGE, HERO_WIDTHS)} sizes={HERO_SIZES} alt="The complete Industrial Instrumentation Instructor System 2026: six core volumes, six bonus guides and printable classroom worksheets" width={1000} height={1000} fetchPriority="high" decoding="async" className="mx-auto -my-4 h-auto max-h-[30vh] w-full max-w-lg object-contain drop-shadow-[0_24px_50px_rgba(0,0,0,0.9)] sm:-my-6 sm:max-h-[34vh]" data-id="element-97" data-mp="358zr0c70zfl1" />
        </Reveal>

        <Reveal index={2} className="flex w-full flex-col items-center" data-id="element-98" data-mp="s2jnhxye22k1">
          <p className="max-w-xl text-sm leading-relaxed text-ink-200 sm:text-base" data-id="element-99" data-mp="s3usu27hfid9">
            6 technical volumes + 6 instructor bonuses. Ready to teach tomorrow.
          </p>

          <ul className="mt-3 flex flex-wrap items-center justify-center gap-x-5 gap-y-1.5 text-xs text-ink-400 sm:text-sm" data-id="element-100" data-mp="ici1dbpliu7r">
            {proofPoints.map(item => <li key={item} className="flex items-center gap-1.5" data-id="element-101" data-mp="1b15ozdxthvoh">
                <CheckIcon className="h-3.5 w-3.5 text-circuit-400" aria-hidden="true" data-id="element-102" data-mp="161t3awiomunh" />
                {item}
              </li>)}
          </ul>

          <CtaButton className="mt-5" label="Get The Full System Now" data-id="element-103" data-mp="111omf5dv3xwq" />

          <p className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 font-display text-xs uppercase tracking-[0.16em] text-ink-500" data-id="element-104" data-mp="x1nv4k5nzs89">
            <span data-id="element-105" data-mp="3lu9xn2arm0br">
              <span className="line-through" data-id="element-106" data-mp="3lqm4i498ffxk">${CORE_PRICE}</span>{' '}
              <span className="text-ink-200" data-id="element-107" data-mp="3hpsgzbzpc357">${SALE_PRICE} today</span> · One-time payment
            </span>
            <span className="flex items-center gap-1.5 normal-case tracking-normal" data-id="element-108" data-mp="1xb5n26cmcdcf">
              <span className="flex" aria-hidden="true" data-id="element-109" data-mp="21bft1j0ev3ra">
                {[0, 1, 2, 3, 4].map(i => <StarIcon key={i} className="h-3.5 w-3.5 fill-ember-400 text-ember-400" data-id="element-110" data-mp="2697lqf8vqtwj" />)}
              </span>
              4.9/5
            </span>
            <span className="flex items-center gap-1.5 normal-case tracking-normal" data-id="element-111" data-mp="2fzsu2uecvcsn">
              <ShieldCheckIcon className="h-3.5 w-3.5 text-circuit-400" aria-hidden="true" data-id="element-112" data-mp="2g1wyddg62dtg" />
              7-day guarantee
            </span>
            <span className="flex items-center gap-1.5 normal-case tracking-normal" data-id="element-113" data-mp="2p08utw3ps5tt">
              <DownloadIcon className="h-3.5 w-3.5 text-circuit-400" aria-hidden="true" data-id="element-114" data-mp="2tzbokke8upmh" />
              Instant download
            </span>
          </p>
        </Reveal>
      </div>

      <div className="relative border-t border-white/5 bg-steel-900/60" data-id="element-115" data-mp="uou0t33o7k89">
        <ul className="mx-auto flex max-w-6xl flex-wrap items-center justify-center gap-x-8 gap-y-2 px-6 py-3" data-id="element-116" data-mp="zlsxntepy5a5">
          {standards.map(({
          code,
          label
        }) => <li key={code} className="flex items-baseline gap-2" data-id="element-117" data-mp="3vgvtuirmsz1i">
              <span className="font-display text-base font-semibold uppercase tracking-wider text-ink-50" data-id="element-118" data-mp="e9aaofeipiqf">
                {code}
              </span>
              <span className="text-xs uppercase tracking-[0.14em] text-ink-500" data-id="element-119" data-mp="9b8ohllsp3aq">{label}</span>
            </li>)}
        </ul>
      </div>
    </header>;
}